/* A compact map for the bus page — where this one bus is, and the path it has
 * drawn on the running route.
 *
 * NOT a second Live Map. Live Map is the fleet view with search, filters and
 * every bus; this is one bus, no chrome, sized to sit beside the run timeline.
 * It shares `GOOGLE_MAPS_SCRIPT_ID` with every other map in the dashboard —
 * loading the Maps script twice under different ids is what breaks it.
 *
 * It FOLLOWS the bus by default and offers Re-centre once you pan away. That is
 * deliberate: on the 2026-07-30 drive the parent map neither followed the bus
 * nor responded to its re-centre button, while the admin map followed fine — so
 * a map that quietly stops tracking is a known way for this to fail. Following
 * is the default here, and the button only exists once you have taken over.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { GoogleMap, Marker, Polyline, useJsApiLoader } from '@react-google-maps/api';
import {
  GOOGLE_MAPS_LIBRARIES, GOOGLE_MAPS_SCRIPT_ID, GOOGLE_MAPS_VERSION,
} from '../../utils/googleMapsLoader';
import { simplifyForDisplay, splitRouteSegments } from '../../utils/routeSegments';
import { useGlidingPosition } from '../../hooks/useGlidingPosition';

const NAIROBI = { lat: -1.286, lng: 36.817 };
const ROUTE_BLUE = '#2563eb';

export interface BusMiniMapProps {
  liveLocation?: { lat: number; lng: number } | null;
  routePoints?: { lat: number; lng: number; gapBefore?: boolean; estimated?: boolean }[];
  /** Greys the marker when the bus has stopped reporting. */
  stale?: boolean;
  height?: number;
  /** Frame the whole trail instead of following the marker — the historical
   *  trip view. A loop's endpoints sit metres apart, so centring on the last
   *  point would hide the actual drive (the Analytics map learned this). */
  fitToRoute?: boolean;
  /** Shown when there is nothing to draw. The default copy talks about live
   *  position; a selected historical run needs different words. */
  emptyMessage?: string;
}

export default function BusMiniMap({
  liveLocation, routePoints, stale = false, height = 300,
  fitToRoute = false, emptyMessage,
}: BusMiniMapProps) {
  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY ?? '';
  const { isLoaded, loadError } = useJsApiLoader({
    id: GOOGLE_MAPS_SCRIPT_ID,
    googleMapsApiKey: apiKey,
    libraries: GOOGLE_MAPS_LIBRARIES,
    version: GOOGLE_MAPS_VERSION,
  });

  const mapRef = useRef<google.maps.Map | null>(null);
  const [following, setFollowing] = useState(true);

  const pts = useMemo(() => routePoints ?? [], [routePoints]);
  // Draw-time smoothing only — the stored trail keeps every point.
  const segments = useMemo(
    () => splitRouteSegments(pts).map((s) => simplifyForDisplay(s)),
    [pts],
  );
  const rawMarker = liveLocation ?? (pts.length ? pts[pts.length - 1] : null);
  // Same glide as the live panel and both mobile maps — drawn position only.
  const marker = useGlidingPosition(rawMarker) ?? rawMarker;
  const center = marker ?? NAIROBI;

  const onLoad = useCallback((map: google.maps.Map) => { mapRef.current = map; }, []);

  /* Only a human drag stops the follow. Using the generic `center_changed`
     would cancel it on our own panTo, so the map would follow exactly once.

     Following then resumes ten seconds after the last drag (Gabriel,
     2026-08-02) rather than waiting for an explicit re-centre. Looking away is
     nearly always a glance — at where a trip started, at a junction — and
     having to press a button to start following again is a second chore after
     the glance. The same rule runs on BusLiveMapPanel so the two agree. */
  const FOLLOW_RESUME_MS = 10_000;
  const resumeTimerRef = useRef<number | null>(null);

  const onDragStart = useCallback(() => {
    setFollowing(false);
    if (resumeTimerRef.current) window.clearTimeout(resumeTimerRef.current);
    resumeTimerRef.current = window.setTimeout(
      () => setFollowing(true),
      FOLLOW_RESUME_MS,
    );
  }, []);

  useEffect(() => () => {
    if (resumeTimerRef.current) window.clearTimeout(resumeTimerRef.current);
  }, []);

  useEffect(() => {
    if (fitToRoute) return; // the trail is the subject, not the marker
    if (!following || !mapRef.current || !marker) return;
    mapRef.current.panTo(marker);
  }, [following, marker, fitToRoute]);

  /* Historical-trip mode: frame the trail once per trail. */
  useEffect(() => {
    if (!fitToRoute || !mapRef.current || pts.length < 2 || !window.google) return;
    const b = new window.google.maps.LatLngBounds();
    pts.forEach((pt) => b.extend(pt));
    mapRef.current.fitBounds(b, 40);
  }, [fitToRoute, pts]);

  const recentre = () => {
    setFollowing(true);
    if (mapRef.current && marker) { mapRef.current.panTo(marker); mapRef.current.setZoom(15); }
  };

  const hasEstimated = segments.some((s) => s.length >= 2 && s[0]?.estimated);

  if (loadError) {
    return (
      <div className="fd-minimap-msg" style={{ height }}>
        The map could not load. Everything else on this page still works.
      </div>
    );
  }
  if (!isLoaded) {
    return <div className="fd-minimap-msg" style={{ height }}>Loading the map…</div>;
  }
  if ((fitToRoute && pts.length < 2) || (!marker && !pts.length)) {
    return (
      <div className="fd-minimap-msg" style={{ height }}>
        {emptyMessage ?? 'No position recorded for this bus yet.'}
      </div>
    );
  }

  return (
    <div className="fd-minimap" style={{ height }}>
      <GoogleMap
        mapContainerStyle={{ width: '100%', height: '100%' }}
        center={center}
        zoom={15}
        onLoad={onLoad}
        onDragStart={onDragStart}
        options={{
          disableDefaultUI: true,
          clickableIcons: false,
          gestureHandling: 'greedy',
        }}
      >
        {segments.flatMap((seg, i) => {
          if (seg.length < 2) return [];
          /* Dotted = inferred. The app was closed for this stretch, so it is the
             likely road route rather than recorded GPS, and it must never be
             drawn as though it were measured. */
          if (seg[0]?.estimated) {
            return [
              <Polyline
                key={`est-${i}`}
                path={seg}
                options={{
                  strokeOpacity: 0,
                  geodesic: true,
                  zIndex: 2,
                  icons: [{
                    icon: {
                      path: google.maps.SymbolPath.CIRCLE,
                      fillColor: ROUTE_BLUE, fillOpacity: 1,
                      strokeColor: ROUTE_BLUE, strokeOpacity: 1, scale: 2.6,
                    },
                    offset: '0',
                    repeat: '14px',
                  }],
                }}
              />,
            ];
          }
          return [
            <Polyline
              key={`glow-${i}`}
              path={seg}
              options={{ strokeColor: ROUTE_BLUE, strokeOpacity: 0.18, strokeWeight: 9, zIndex: 1 }}
            />,
            <Polyline
              key={`line-${i}`}
              path={seg}
              options={{ strokeColor: ROUTE_BLUE, strokeOpacity: 0.95, strokeWeight: 3.5, zIndex: 2 }}
            />,
          ];
        })}

        {/* Start / end dots — every route line carries them (Gabriel,
            2026-08-24). Live mode keeps the moving bus marker as its "end". */}
        {pts.length >= 2 && (
          <Marker
            position={{ lat: pts[0].lat, lng: pts[0].lng }}
            zIndex={3}
            icon={{
              path: google.maps.SymbolPath.CIRCLE,
              scale: 6.5,
              fillColor: '#ffffff',
              fillOpacity: 1,
              strokeColor: ROUTE_BLUE,
              strokeWeight: 3.5,
            }}
          />
        )}
        {fitToRoute && pts.length >= 2 && (
          <Marker
            position={{ lat: pts[pts.length - 1].lat, lng: pts[pts.length - 1].lng }}
            zIndex={3}
            icon={{
              path: google.maps.SymbolPath.CIRCLE,
              scale: 6.5,
              fillColor: ROUTE_BLUE,
              fillOpacity: 1,
              strokeColor: '#ffffff',
              strokeWeight: 3,
            }}
          />
        )}
        {marker && (
          <Marker
            position={marker}
            icon={{
              path: google.maps.SymbolPath.CIRCLE,
              scale: 8,
              fillColor: stale ? '#9a93b3' : '#16a34a',
              fillOpacity: 1,
              strokeColor: '#ffffff',
              strokeWeight: 3,
            }}
          />
        )}
      </GoogleMap>

      {!following && (
        <button type="button" className="fd-recentre" onClick={recentre}>
          Re-centre
        </button>
      )}

      {hasEstimated && (
        <div className="fd-maplegend">
          <span className="fd-dotline" />
          <span><b>Dotted</b> — the app was closed here, so this is the likely route, not recorded GPS.</span>
        </div>
      )}
    </div>
  );
}
